import React, { useState, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import CustomModal from "./customModal";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useProfileAndSettings } from "../../../hooks/useProfileAndSettings";
import { getCurrencySymbol } from "../../../utils/currencyDisplay";

export default function ProductMultiSelectionModal({
  showModal,
  setShowModal,
  showButton,
  onSubmit,
  setSelectedProducts,
  selectedProducts,
  products,
  maxSelection = null, // null = unlimited, 1 = single selection
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [tempSelectedProducts, setTempSelectedProducts] = useState(selectedProducts || []);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const currencySymbol = getCurrencySymbol(getCompCurrency());

  

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product => {
        const productName = product.menuItem_Name || product.MenuItem_Name || product.name || '';
        return productName.toLowerCase().includes(searchTerm.trim().toLowerCase());
      });
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  useEffect(() => {
    // Normalize selectedProducts to always be array of IDs
    const normalized = (selectedProducts || []).map(p => {
      if (typeof p === 'object') {
        return p.menuItem_ID || p.MenuItem_ID || p.id;
      }
      return p;
    });
    setTempSelectedProducts(normalized);
  }, [selectedProducts, showModal]);

  const handleSubmit = () => {
    // Normalize to ensure we're passing IDs, not objects
    const normalized = tempSelectedProducts.map(p => {
      if (typeof p === 'object') {
        return p.menuItem_ID || p.MenuItem_ID || p.id;
      }
      return p;
    });
    
    if (typeof onSubmit === "function") {
      onSubmit(normalized);
    }
    setSelectedProducts(normalized); 
    setShowModal(false);
  };

  const handleProductSelection = (product) => {
    // Get product ID handling both field name formats
    const productId = product.menuItem_ID || product.MenuItem_ID || product.id;
    
    setTempSelectedProducts(prev => {
      const index = prev.findIndex(p => {
        // Handle both object and ID comparisons
        const prevId = typeof p === 'object' 
          ? (p.menuItem_ID || p.MenuItem_ID || p.id)
          : p;
        
        // Try multiple comparison methods
        if (prevId == productId) return true; // Loose equality
        if (parseInt(prevId) === parseInt(productId)) return true;
        if (Number(prevId) === Number(productId)) return true;
        if (String(prevId).trim() === String(productId).trim()) return true;
        
        return false;
      });
      
      if (index > -1) {
        // Item is already selected, remove it
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      } else {
        // Item is not selected
        if (maxSelection === 1) {
          // Single selection mode - replace with new selection
          return [productId];
        } else if (maxSelection && prev.length >= maxSelection) {
          // Max selection reached, don't add
          return prev;
        } else {
          // Add to selection
          return [...prev, productId];
        }
      }
    });
  };

  const RenderProductCard = ({ product }) => {
    // Get product ID handling both field name formats
    const productId = product.menuItem_ID || product.MenuItem_ID || product.id;
    
    const isSelected = tempSelectedProducts.some(p => {
      // Handle both object and ID comparisons
      const pId = typeof p === 'object' 
        ? (p.menuItem_ID || p.MenuItem_ID || p.id)
        : p;
      
      // Try multiple comparison methods
      if (pId == productId) return true; // Loose equality
      if (parseInt(pId) === parseInt(productId)) return true;
      if (Number(pId) === Number(productId)) return true;
      if (String(pId).trim() === String(productId).trim()) return true;
      
      return false;
    });
return (
      <div 
        className="product-card"
        style={{
          background: isSelected 
            ? (isDarkMode ? `${themeColor}20` : `${themeColor}15`) 
            : (isDarkMode ? '#2d3748' : '#ffffff'),
          borderRadius: '12px',
          padding: '16px 20px',
          border: isSelected 
            ? `2px solid ${themeColor}` 
            : `2px solid ${isDarkMode ? '#4a5568' : '#e9ecef'}`,
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          boxShadow: isSelected 
            ? `0 4px 12px ${themeColor}40` 
            : (isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'),
          marginBottom: '12px'
        }}
        onClick={() => handleProductSelection(product)}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = themeColor;
            e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}40`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
            e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          
          {/* Product Icon */}
          <div 
            style={{
              width: '40px',
              height: '40px',
              background: isSelected 
                ? themeColor 
                : (isDarkMode ? '#4a5568' : '#f8f9fa'),
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              border: `1px solid ${isDarkMode ? '#718096' : '#e9ecef'}`,
              overflow: 'hidden'
            }}
          >
            <LazyLoadImage
              src={product.menuItem_Image || product.MenuItem_Image || "/assets/img/default-product.png"}
              alt={product.menuItem_Name || product.MenuItem_Name || product.name || 'Product'}
              style={{
                height: "32px",
                width: "32px",
                objectFit: "contain",
              }}
            />
          </div>
          
          {/* Product Info */}
          <div className="flex-grow-1">
            <span 
              style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                color: isSelected 
                  ? themeColor 
                  : (isDarkMode ? '#e2e8f0' : '#495057'),
                display: 'block',
                marginBottom: '2px'
              }}
            >
              {product.menuItem_Name || product.MenuItem_Name || product.name || 'Unknown Product'}
            </span>
            {(product.menuItem_Price || product.MenuItem_Price || product.menuItem_SalePrice || product.MenuItem_SalePrice) && (
              <small 
                style={{ 
                  color: isSelected 
                    ? themeColor 
                    : (isDarkMode ? '#a0aec0' : '#6c757d'),
                  fontSize: '11px',
                  lineHeight: '1.3'
                }}
              >
                {currencySymbol}{product.menuItem_Price || product.MenuItem_Price || product.menuItem_SalePrice || product.MenuItem_SalePrice}
              </small>
            )}
          </div>
        </div>
        
        {/* Custom Checkbox */}
        <div 
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            border: isSelected 
              ? `2px solid ${themeColor}` 
              : `2px solid ${isDarkMode ? '#718096' : '#e9ecef'}`,
            background: isSelected ? themeColor : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          {isSelected && (
            <i 
              className="fa fa-check" 
              style={{ 
                fontSize: '10px',
                color: 'white' 
              }}
            ></i>
          )}
        </div>
      </div>
    );
  };

  // Return CustomModal directly without wrapper div
  return (
    <CustomModal
      isOpen={showModal}
      title="Select Products"
      onClose={() => setShowModal(false)}
      onSubmit={handleSubmit}
      showButton={showButton}
      showSubmitButton={true}
      isCentered={true}
      submitButtonText="Select"
      themeColor={themeColor}
    >
      {/* Product Selection Modal */}
      <p style={{ 
        color: isDarkMode ? '#a0aec0' : '#6c757d', 
        marginBottom: '20px' 
      }}>
        {maxSelection === 1 
          ? 'Select one product (single selection only)'
          : maxSelection 
            ? `Select up to ${maxSelection} products`
            : 'Select the products for the item'}
      </p>
      
      {/* Search Input */}
      <div className="mb-4">
        <div 
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '12px 16px',
              border: `2px solid ${isDarkMode ? '#4a5568' : '#e9ecef'}`,
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
              color: isDarkMode ? '#e2e8f0' : '#495057'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = themeColor;
              e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
              e.target.style.boxShadow = 'none';
            }}
          />
          <i 
            className="fa fa-search" 
            style={{
              position: 'absolute',
              right: '16px',
              color: isDarkMode ? '#a0aec0' : '#6c757d',
              fontSize: '14px'
            }}
          ></i>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                position: 'absolute',
                right: '40px',
                background: 'transparent',
                border: 'none',
                color: isDarkMode ? '#a0aec0' : '#6c757d',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#4a5568' : '#f8f9fa';
                e.currentTarget.style.color = isDarkMode ? '#e2e8f0' : '#495057';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = isDarkMode ? '#a0aec0' : '#6c757d';
              }}
            >
              <i className="fa fa-times" style={{ fontSize: '12px' }}></i>
            </button>
          )}
        </div>
      </div>
      
      {/* Product Cards */}
      <div style={{ height: "450px", overflowY: "auto", paddingRight: '8px' }}>
        {filteredProducts && filteredProducts.length > 0 ? (
          <div>
            {filteredProducts.map((product) => {
              const productKey = product.menuItem_ID || product.MenuItem_ID || product.id;
              return <RenderProductCard key={productKey} product={product} />;
            })}
          </div>
        ) : (
          <div 
            className="text-center py-5"
            style={{
              background: isDarkMode ? '#2d3748' : '#f8f9fa',
              borderRadius: '12px',
              border: `2px dashed ${isDarkMode ? '#4a5568' : '#e9ecef'}`
            }}
          >
            <div 
              style={{
                width: '60px',
                height: '60px',
                background: isDarkMode ? '#4a5568' : '#ffffff',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: `1px solid ${isDarkMode ? '#718096' : '#e9ecef'}`
              }}
            >
              <i className="fa fa-box" style={{ 
                fontSize: '24px', 
                color: isDarkMode ? '#a0aec0' : '#6c757d' 
              }}></i>
            </div>
            <p style={{ 
              color: isDarkMode ? '#a0aec0' : '#6c757d', 
              fontSize: '13px', 
              marginBottom: '6px' 
            }}>
              No products found
              {searchTerm.length > 0 ? ` matching "${searchTerm}"` : ""}
            </p>
            {searchTerm.length > 0 && (
              <small style={{ 
                color: isDarkMode ? '#a0aec0' : '#6c757d', 
                fontSize: '11px' 
              }}>
                Try adjusting your search terms
              </small>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
}